import React, { useCallback } from 'react';
import { GoogleMap as Map, useLoadScript } from '@react-google-maps/api';
import MapLoadingState from './MapLoadingState';
import MapError from './MapError';
import { logError, logInfo } from '../../../../utils/logger';

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

const mapContainerStyle = {
  width: '100%',
  height: '100%'
};

const defaultCenter = {
  lat: 45.5017,
  lng: -73.5673
};

const mapOptions = {
  zoomControl: false,
  mapTypeControl: false,
  streetViewControl: false,
  fullscreenControl: false,
  gestureHandling: 'greedy',
  styles: [
    {
      featureType: 'poi',
      stylers: [{ visibility: 'off' }]
    }
  ]
};

const libraries = ['places', 'drawing', 'geometry'];

const GoogleMap = ({ 
  onMapLoad,
  children,
  center = defaultCenter,
  onClick,
  mapType = 'roadmap',
  showTraffic = false,
  showStreetView = false,
  mode = 'pan'
}) => {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries,
    version: "weekly",
    region: "CA",
    language: "en",
    preventGoogleFontsLoading: true
  });

  const handleLoad = useCallback((map) => {
    if (!map) {
      logError('Map instance not provided to handleLoad');
      return;
    }

    logInfo('Map instance loaded');
    
    // Configure Street View control position
    const streetViewControlOptions = {
      position: google.maps.ControlPosition.TOP_RIGHT
    };

    map.setOptions({
      streetViewControl: showStreetView,
      streetViewControlOptions,
      draggable: mode === 'pan'
    });

    // Ensure Street View control is above other UI elements
    const streetViewControl = map.getDiv().querySelector('button[title="Street View"]');
    if (streetViewControl) {
      streetViewControl.style.zIndex = '1000';
    }

    onMapLoad?.(map);
  }, [onMapLoad, showStreetView, mode]);

  if (loadError) {
    logError('Google Maps failed to load:', loadError);
    return <MapError message="Failed to load map. Please check your internet connection and try again." />;
  }

  if (!isLoaded) {
    return <MapLoadingState />;
  }

  return (
    <Map
      mapContainerStyle={mapContainerStyle}
      center={center}
      zoom={15}
      options={{
        ...mapOptions,
        mapTypeId: mapType,
        draggableCursor: mode !== 'pan' ? 'crosshair' : 'grab',
        draggingCursor: mode !== 'pan' ? 'crosshair' : 'grabbing',
        streetViewControl: showStreetView,
        draggable: mode === 'pan'
      }}
      onLoad={handleLoad}
      onClick={onClick}
    >
      {children}
    </Map>
  );
};

export default React.memo(GoogleMap);